<template>
  <div class="dash">
    <!-- STICKY TOP: Tabelle 1 bleibt immer sichtbar -->
    <div class="dash__top">
      <v-data-table
        class="elevation-1 vtable-tight"
        density="compact"
        mobile-breakpoint="0"
        hide-default-footer
        :headers="headersV02"
        :items="aggregatedByUser"
        :loading="loading"
        @click:row="handleClickUserRow"
      >
        <template #item.username="{ value, item }">
          <span
            class="user-avatar"
            :style="{ backgroundColor: getUserAvatarColor(value, item.userId) }"
            :title="value"
            role="img"
            :aria-label="value || 'Unbekannter User'"
          >
            {{ Array.from(String(value || '').trim())[0]?.toLocaleUpperCase('de-DE') || '?' }}
          </span>
        </template>

        <template #item.resultSum="{ value }">
          <span class="euro-cell" :class="getSignedClass(value)">
            {{ formatKiloEuro(value) }}
          </span>
        </template>

        <template #item.virtDelta1="{ value }">
          <span class="pct-pill" :class="getSignedClass(value)">{{ formatPercent(value) }}</span>
        </template>
        <template #item.virtDelta5="{ value }">
          <span class="pct-pill" :class="getSignedClass(value)">{{ formatPercent(value) }}</span>
        </template>
        <template #item.virtDelta20="{ value }">
          <span class="pct-pill" :class="getSignedClass(value)">{{ formatPercent(value) }}</span>
        </template>

        <template #item.totalValue="{ value }">
          <span class="euro-cell" :class="getSignedClass(value)">
            {{ formatKiloEuro(value) }}
          </span>
        </template>

        <template #item.rohstoffeSum="{ value }">
          <span class="euro-cell" :class="getSignedClass(value)">
            {{ formatKiloEuro(value) }}
          </span>
        </template>

        <template #item.totalValueOhneRohstoff="{ value }">
          <span :class="getSignedClass(value)">
            {{ formatKiloEuro(value) }}
          </span>
        </template>

        <template #item.realisiertSum="{ value }">
          <span class="euro-cell" :class="getSignedClass(value)">
            {{ formatKiloEuro(value) }}
          </span>
        </template>

        <template #item.einstandswertSum="{ value }">
          <span class="euro-cell" :class="getSignedClass(value)">
            {{ formatKiloEuro(value) }}
          </span>
        </template>

        <template #no-data>
          <div class="pa-4 text-medium-emphasis">
            Keine Daten - bitte "Reload" klicken.
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Benutzer, Analyse und Cash -->
    <v-card class="dashboard-controls" variant="outlined">
      <section class="dashboard-user-filter" aria-labelledby="dashboard-users-title">
        <h2 id="dashboard-users-title" class="dashboard-controls__title">Benutzer</h2>
        <v-chip-group v-model="selectedUsers" multiple class="dashboard-users" aria-label="Benutzer auswählen">
          <v-chip
            v-for="name in Object.keys(Users)"
            :key="name"
            :value="Number(Users[name][0].user_iduser.iduser)"
            filter
            variant="outlined"
          >
            {{ name }}
          </v-chip>
        </v-chip-group>
        <p v-if="!selectedUsers.length" class="dashboard-controls__hint">Alle Benutzer ausgewählt</p>
      </section>

      <v-divider class="dashboard-controls__divider" />

      <div class="dashboard-controls__bottom">
        <section class="dashboard-analysis" aria-labelledby="dashboard-analysis-title">
          <h2 id="dashboard-analysis-title" class="dashboard-controls__title">Aktienanalyse</h2>
          <div class="dashboard-analysis__actions">
            <v-select
              v-model="analyzerStockTypes"
              :items="['ETF', 'Aktie', 'Rohstoff']"
              label="Anlagetyp"
              placeholder="Alle Anlagetypen"
              persistent-placeholder
              multiple
              density="compact"
              chips
              closable-chips
              clearable
              variant="filled"
              hide-details
              class="dashboard-analysis__select"
            />
            <v-btn
              class="dashboard-controls__button"
              title="Aktien analysieren"
              aria-label="Aktien analysieren"
              color="primary"
              variant="flat"
              :disabled="!canAnalyzeAllStocks"
              @click="handleAnalyzeAllStocks"
            >
              <v-icon class="dashboard-analysis-icon" icon="mdi-chart-line" />
              <span class="dashboard-action-label">Analysieren</span>
            </v-btn>
            <v-btn
              class="dashboard-all-stocks-button"
              color="primary"
              :variant="showAllStocks ? 'flat' : 'tonal'"
              :aria-pressed="showAllStocks"
              @click="toggleAllStocks"
            >
              Alle Stocks
            </v-btn>
          </div>
        </section>

        <section class="dashboard-cash" aria-labelledby="dashboard-cash-title">
          <h2 id="dashboard-cash-title" class="dashboard-controls__title">Cash</h2>
          <div class="dashboard-cash__actions">
            <span class="ccash-current">{{ ccashCurrentEuro || '-' }}</span>
            <CashToWealthAction button-color="primary" button-variant="flat" compact-mobile mobile-button-label="überweisen" @completed="load" />
          </div>
        </section>
      </div>
    </v-card>

    <!-- MAIN: Tabelle + Charts in Tabs -->
    <div class="dash__main-tabs">
      <v-card class="elevation-1" variant="flat">
        <v-tabs v-model="contentTab" grow>
          <v-tab value="table">Positionen</v-tab>
          <v-tab value="charts">Charts</v-tab>
        </v-tabs>

        <v-divider />

        <v-window
          v-model="contentTab"
          :touch="false"
        >
          <!-- TAB 1: Untere Tabelle -->
          <v-window-item value="table">
            <section class="dash__table pa-0">
              <v-card class="elevation-0" variant="flat">
                <div v-if="showAllStocks" class="pa-3">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="searchQuery"
                        label="Search"
                        density="compact"
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="SchwellwertAktuellerKurs"
                        label="Mindestpositionswert (€)"
                        type="number"
                        density="compact"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </div>

                <v-divider v-if="showAllStocks" />

                <v-data-table
                  class="text-caption mobile-nowrap lower-table"
                  :headers="headers"
                  item-key="idstocks"
                  :items="array_allStocksFilteredSchwellwert"
                  :search="showAllStocks ? searchQuery : ''"
                  :items-per-page="showAllStocks ? -1 : 40"
                  :items-per-page-options="[10, 20, 40, 100, { value: -1, title: 'All' }]"
                  density="compact"
                  mobile-breakpoint="0"
                  @click:row="handleClickActivty"
                >
                  <template #item.stocks_name="{ item }">
                    <span class="stock-name-cell" :title="item.stocks_name" v-text="item.stocks_name"></span>
                  </template>

                  <template #item.stock_delta1_prozent="{ item }">
                    <div :class="getColor(item.stock_delta1_prozent)">
                      <span v-text="Math.ceil(item.stock_delta1_prozent * 100) / 100 + '%'"></span>
                    </div>
                  </template>

                  <template #item.stock_delta5_prozent="{ item }">
                    <div :class="getColor(item.stock_delta5_prozent)">
                      <span v-text="Math.ceil(item.stock_delta5_prozent * 100) / 100 + '%'"></span>
                    </div>
                  </template>

                  <template #item.stock_delta20_prozent="{ item }">
                    <div :class="getColor(item.stock_delta20_prozent)">
                      <span v-text="Math.ceil(item.stock_delta20_prozent * 100) / 100 + '%'"></span>
                    </div>
                  </template>

                  <template #item.stock_Aktuellerkurs_gesamt="{ item }">
                    <span v-text="Math.ceil(item.stock_Aktuellerkurs_gesamt) + ' €'"></span>
                  </template>

                  <template #item.delta="{ item }">
                    <div :class="getColor(item.delta)">
                      <span v-text="Math.ceil(item.delta * 100) / 100 + ' €'"></span>
                    </div>
                  </template>

                  <template #item.stocks_Menge="{ item }">
                    <div :class="getColor(item.stocks_Menge)">
                      <span v-text="Math.ceil(item.stocks_Menge * 100) / 100"></span>
                    </div>
                  </template>
                </v-data-table>
              </v-card>
            </section>
          </v-window-item>

<!-- TAB 2: Charts -->
<v-window-item value="charts">
  <section class="dash__charts pa-3">
    <v-card class="elevation-0" variant="flat">
      <div class="pa-3">
        <v-card class="pa-3 mb-3" variant="tonal">
          <div class="dashboard-chart-actions">
            <v-btn
              class="abtn abtn--percent"
              :class="{ 'abtn--active': aggregateChartPercent }"
              density="compact"
              size="small"
              @click="toggleAggregateChartPercent"
            >
              %
            </v-btn>
            <v-btn
              class="abtn"
              :class="{ 'abtn--active': aggregateChartMode === 'monthly' && aggregateChartMonths === 12 }"
              density="compact"
              size="small"
              @click="setAggregateChartMonths(12)"
            >
              1Y
            </v-btn>
            <v-btn
              class="abtn"
              :class="{ 'abtn--active': aggregateChartMode === 'monthly' && aggregateChartMonths === 36 }"
              density="compact"
              size="small"
              @click="setAggregateChartMonths(36)"
            >
              3Y
            </v-btn>
            <v-btn
              class="abtn abtn--mode"
              :class="{ 'abtn--active': aggregateChartMode === 'daily' }"
              density="compact"
              size="small"
              @click="setAggregateChartDaily"
            >
              DAY
            </v-btn>
            <v-btn
              class="abtn abtn--mode"
              :class="{ 'abtn--active': aggregateChartMode === 'monthly' && !aggregateChartMonths }"
              density="compact"
              size="small"
              @click="setAggregateChartMonthly"
            >
              MONTH
            </v-btn>
          </div>

          <div class="text-subtitle-2 mb-2">
            Gruppenübersicht: Rohstoff / ETF / Aktie
          </div>
          <div class="chart-box chart-box--timeline">
            <canvas ref="aggregateClassCanvas" class="chart-canvas" />
          </div>
        </v-card>

        <v-card class="pa-3 mb-3" variant="tonal">
          <div class="text-subtitle-2 mb-2">
            Relativer Gewinn % je Aktie (Profit / Einstand)
          </div>
          <div class="chart-box chart-box--tall">
            <canvas ref="barRelCanvas" class="chart-canvas" />
          </div>
        </v-card>

        <v-card class="pa-3 mb-3" variant="tonal">
          <div class="text-subtitle-2 mb-2">
            Verteilung % nach Aktientyp
          </div>
          <div class="chart-box chart-box--square">
            <canvas ref="typePieCanvas" class="chart-canvas" />
          </div>
        </v-card>

        <v-card class="pa-3 mb-3" variant="tonal">
          <div class="text-subtitle-2 mb-2">
            Gesamtwert € - Verteilung je Aktie
          </div>
          <div class="chart-box chart-box--square">
            <canvas ref="pieCanvas" class="chart-canvas" />
          </div>
        </v-card>

        <v-card class="pa-3" variant="tonal">
          <div class="text-subtitle-2 mb-2">
            Delta € je Aktie
          </div>
          <div class="chart-box chart-box--wide">
            <canvas ref="barCanvas" class="chart-canvas" />
          </div>
        </v-card>
      </div>
    </v-card>
  </section>
</v-window-item>
        </v-window>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { getUserAvatarColor } from '@/utils/userSeriesColors';
import CashToWealthAction from '@/components/CashToWealthAction.vue';
import _ from 'lodash'
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables);

export default {
  components: { CashToWealthAction },
  name: 'DashboardScreen',
  data() {
    return {
      error: null,
      loading: false,
      chartRel: null,
      chartAggregateClasses: null,
      aggregateChartPercent: false,
      aggregateChartMode: "monthly",
      aggregateChartMonths: null,
      aggregateChartDailyDays: 92,
      chartTab: "relperf",
      contentTab: "table",
      chartTypePie: null,
      isRenderingCharts: false,
      headersV02: [
          { title: "Us",      key: "username",         align: "start", width: "44px" },
          { title: "Virt.",   key: "resultSum",        align: "start", width: "72px" },
          { title: "All",     key: "totalValue",       align: "start", width: "72px" },
          { title: "Tag",     key: "virtDelta1",       align: "start", width: "52px" },
          { title: "Woche",   key: "virtDelta5",       align: "start", width: "56px" },
          { title: "Monat",   key: "virtDelta20",      align: "start", width: "56px" },
          { title: "Depot",   key: "totalValueOhneRohstoff", align: "start", width: "72px" },
          { title: "Rohst.",  key: "rohstoffeSum",     align: "start", width: "72px" },
          { title: "Real.",   key: "realisiertSum",    align: "start", width: "72px" },
          { title: "Einstand",key: "einstandswertSum", align: "start", width: "80px" },
        ],
      form: {
        title: '',
        content: '',
      },
      chartPie: null,
      chartBar: null,
        array_allStocks: [],
        array_allAggragtes: [],
        array_allRohstoffe: [],
        virtualRows:[],
        DepotKursRows:[],
        Users:[],
        UsersCheckBoxStatus:[5, 7, 3, 1],
        array_allKurseGesamt: [],
        array_allDelta: [],
        SchwellwertAktuellerKurs: 0,
      searchQuery: '',
      showAllStocks: false,
      selectedUsers: [],
      analyzerStockTypes: [],
      searchUser: '',
      groupBy: [{ key: 'user_iduser.user_username' }],
      headers: [
        {
          title: 'name',
          key: 'stocks_name',

        },
        { title: 'Delta T %',key: 'stock_delta1_prozent',align: 'end'},
        { title: 'Delta W %',key: 'stock_delta5_prozent',align: 'end'},
        { title: 'Delta M %',key: 'stock_delta20_prozent',align: 'end'},
        { title: 'Delta gesamt €',
            key: 'delta',

            align: 'end'},
        { title: 'Aktuell gesamt €',
            key: 'stock_Aktuellerkurs_gesamt',
            align: 'end'},
        { title: 'User', key: 'user_iduser.user_username',align: 'start'},
        { title: 'Kaufkurs', key: 'stocks_Kaufkurs',align: 'end'},
        { title: 'Aktuellerkurs', key: 'stock_Aktuellerkurs',align: 'end'},
        { title: 'Menge', key: 'stocks_Menge',align: 'end'},
      ],
        pagination: {
          rowsPerPage: -1
        },
        latestGoogleupdate :'',
        pagination_aggregates : {'sortBy': 'stock_username', 'descending': true, 'rowsPerPage': -1},
        headers_Aggregates: [
          {
            text: 'stock_username ',
            align: 'left',
            sortable: true,
            value: 'stock_username',
            filter: value => {
              console.log('Filtervalues:',this.Users)
              console.log('UsersCheckBoxStatus:',this.UsersCheckBoxStatus)
              //console.log('Zeilenvalue:',value)


              var trefferFlag = false

              this.UsersCheckBoxStatus.forEach((item) => {
                    //console.log('item',item)
                    if (item === Number(this.Users[value]?.[0]?.user_iduser?.iduser)){
                      //console.log('Treffer')
                      trefferFlag = true
                    }
                  //console.log('item',item);
                })
              //return true

              return trefferFlag



            },
          },
          {
            text: 'stocks_name',
            align: 'left',
            sortable: true,
            value: 'stocks_name'
          },
          { text: 'Delta gesamt €', value: 'stock_Aktuellerkurs',align: 'right'},
          { text: 'Delta T %', value: 'stock_delta1_prozent',align: 'right'},
          { text: 'Delta W %', value: 'stock_delta5_prozent',align: 'right'},
          { text: 'Delta M %', value: 'stock_delta20_prozent',align: 'right'},


        ],
    };
  },
  created: function() {
    console.log('Bin in Dasboard')

  },
  mounted: function(){

      //this.getDataFromApi()
    this.load();


  },
beforeUnmount() {
  this.destroyChart("chartPie");
  this.destroyChart("chartBar");
  this.destroyChart("chartRel");
  this.destroyChart("chartAggregateClasses");
  this.destroyChart("chartTypePie");
},
watch: {
  contentTab: {
    immediate: false,
    async handler(val) {
      if (val === "charts") {
        await this.renderAllCharts();
      }
    },
  },

  searchQuery() {
    if (this.contentTab === "charts") this.renderAllCharts();
  },

  SchwellwertAktuellerKurs() {
    if (this.contentTab === "charts") this.renderAllCharts();
  },

  showAllStocks() {
    if (this.contentTab === "charts") this.renderAllCharts();
  },

  selectedUsers: {
    deep: true,
    handler() {
      if (this.contentTab === "charts") this.renderAllCharts();
    },
  },
},
computed: {
   ...mapGetters({ stocks: 'stateStocks'}),
   // filtered array_allStocks; updates anytime `this.search` changes
      array_allStocksFilteredSchwellwert() {
          //console.log('arr',this.array_allStocks[0])
          const list = this.array_allStocks[0] || [];
          const threshold = Number(this.SchwellwertAktuellerKurs || 0);
          const passesThreshold = d => this.showAllStocks
            ? (!threshold || Number(d.stock_Aktuellerkurs_gesamt) > threshold)
            : Number(d.stock_Aktuellerkurs_gesamt) > 0;
          const passesUsers = d => this.selectedUsers.length === 0
            || this.selectedUsers.includes(Number(d.user_iduser?.iduser));

          return list.filter(d => !String(d.stocks_name || "").startsWith("0_") && passesThreshold(d) && passesUsers(d));
      },
      visibleStocksForAnalyzer() {
        return (this.stocksRaw || []).filter((item) =>
          !String(item?.stocks_name || "").startsWith("0_") &&
          (this.selectedUsers.length === 0 || this.selectedUsers.includes(Number(item.user_iduser?.iduser))) &&
          (!this.analyzerStockTypes?.length || this.analyzerStockTypes.some((type) =>
            type.toLowerCase() === String(item.stocks_Typ || '').trim().toLowerCase()
          )) &&
          this.matchesDashboardSearch(item)
        );
      },
      canAnalyzeAllStocks() {
        return this.visibleStocksForAnalyzer.length > 0;
      },
      ccashCurrentEuro() {
        const ccash = (this.stocksRaw || []).find((stock) =>
          String(stock?.stocks_name || "").toLowerCase() === "0_ccash"
        );
        if (!ccash) return "";

        const value = Number(ccash.stock_Aktuellerkurs ?? ccash.stock_Aktuellerkurs_gesamt);
        if (!Number.isFinite(value)) return "";

        return new Intl.NumberFormat("de-DE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      },
          // 1) Rohdaten aus dem Store ziehen (Passe den Pfad an deinen Store an!)
    //    Beispiel: this.$store.state.stocks.items oder this.$store.getters.stocks
    stocksRaw() {
      //return this.$store.state.stocks?.items ?? [];
      return this.stocks
    },

    // 2) Aggregation pro User
aggregatedByUser() {
  const map = new Map();

  for (const s of this.stocksRaw || []) {
    const u = s.user_iduser || {};
    const userId = u.iduser ?? "unknown";

    const menge = Number(s.stocks_Menge ?? 0);
    const aktuellerKurs = Number(s.stock_Aktuellerkurs ?? 0);
    const totalValue = aktuellerKurs * menge;

    const realisiert = Number(s.stock_Realsiert ?? 0);
    const kaufkurs = Number(s.stocks_Kaufkurs ?? 0);
    const einstand = menge * kaufkurs;

    const handelsplatz = String(s.stocks_Handelsplatz || "");
    const isRohstoff = handelsplatz === "Rohstoff"; // falls nötig: handelsplatz.includes("Rohstoff")
    const rohstoffValue = isRohstoff ? totalValue : 0;

    if (!map.has(userId)) {
      map.set(userId, {
        userId,
        username: u.user_username ?? "-",
        fullName: u.full_name ?? "-",

        stockCount: 0,
        sumAktuellerkurs: 0,

        // Summen
        totalValue: 0,                 // All
        totalValueOhneRohstoff: 0,     // ✅ NEU: Depot ohne Rohstoff
        realisiertSum: 0,
        einstandswertSum: 0,
        rohstoffeSum: 0,

        // Ergebnis
        resultSum: 0,

        // Virtuell-Deltas
        virtDelta1: null,
        virtDelta5: null,
        virtDelta20: null,
        _virtDate: null, // intern: damit wir "neueste" auswählen können
      });
    }

    const row = map.get(userId);

    // normale Aggregationen
    row.stockCount += 1;
    row.sumAktuellerkurs += aktuellerKurs;

    row.totalValue += totalValue;
    row.realisiertSum += realisiert;
    row.einstandswertSum += einstand;
    row.rohstoffeSum += rohstoffValue;

    // ✅ NEU: totalValue nur wenn NICHT Rohstoff
    if (!isRohstoff) {
      row.totalValueOhneRohstoff += totalValue;
    }

    // ✅ Virtuell-Deltas: nur wenn stocks_name = "0_cVirtuell"
    if (s.stocks_name === "0_cVirtuell") {
      const d = s.stocks_Kaufdatum ? new Date(s.stocks_Kaufdatum).getTime() : -Infinity;
      const current = row._virtDate ? new Date(row._virtDate).getTime() : -Infinity;

      // nimm den neuesten (oder ersten, wenn kein Datum)
      if (d >= current) {
        row.virtDelta1 = s.stock_delta1_prozent ?? null;
        row.virtDelta5 = s.stock_delta5_prozent ?? null;
        row.virtDelta20 = s.stock_delta20_prozent ?? null;
        row._virtDate = s.stocks_Kaufdatum ?? row._virtDate;
      }
    }
  }

  // ✅ Ergebnis pro User berechnen
  const out = Array.from(map.values());
  for (const row of out) {
    row.resultSum =
      (Number(row.totalValue) || 0) -
      (Number(row.einstandswertSum) || 0) +
      (Number(row.realisiertSum) || 0);
  }

  return out;
},
  },
methods: {
  toggleAllStocks() {
    this.showAllStocks = !this.showAllStocks;
    this.contentTab = 'table';
  },
  ...mapActions(["createNote"]),

  getUserAvatarColor,

  async submit() {
    console.log("DashboardScreen: submit createNote");
    await this.createNote(this.form);
  },

  async load() {
    await this.getDataFromApi();
  },

  raf2() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  },


async renderChartsForTab() {
  await this.renderAllCharts();
},

async renderAllCharts() {
  if (this.isRenderingCharts) return;
  this.isRenderingCharts = true;

  try {
    await this.$nextTick();
    await this.raf2();

    if (this.contentTab !== "charts") return;

    await this.renderAggregateClassLineChart();
    await this.renderRelPerfChart();
    await this.renderPieChart();
    await this.renderTypePieChart();
    await this.renderBarChart();
  } finally {
    this.isRenderingCharts = false;
  }
},


handleClickUserRow(event, row) {
  const item = row?.item || row || {};
  const username = String(item?.username || "").trim();

  if (Number(item?.userId) === 1) {
    const ids = this.getAnalyzerDepotIdsForUsers([1, 7, 5]);

    if (!ids.length) return;

    this.$router.push({
      name: "Analyzer",
      params: { idstocks: String(ids[0]), stocks_name: "0_cVirtuell" },
      query: { ids: ids.join(","), prozent: "0", mode: "day", currentValueLines: "1" },
    });
    return;
  }

  const stock = this.getAnalyzerDepotStockForUser(username);

  if (!stock) return;

  this.$router.push({
    name: "Analyzer",
    params: {
      idstocks: String(stock.idstocks),
      stocks_name: String(stock.stocks_name || "0_DepotKursAktuell"),
    },
  });
},

getAnalyzerDepotStockForUser(username) {
  const wanted = String(username || "").toLowerCase().trim();
  if (!wanted) return null;

  const userStocks = (this.stocksRaw || []).filter((s) =>
    String(s?.user_iduser?.user_username || "").toLowerCase().trim() === wanted
  );

  return userStocks.find((s) => String(s?.stocks_name || "") === "0_DepotKursAktuell")
    || userStocks.find((s) => String(s?.stocks_name || "") === "0_cVirtuell")
    || userStocks.find((s) => !String(s?.stocks_name || "").startsWith("0_"))
    || null;
},

handleAnalyzeAllStocks() {
  const visibleStocks = this.visibleStocksForAnalyzer || [];
  if (!visibleStocks.length) return;

  const ids = visibleStocks
    .map((stock) => Number(stock?.idstocks))
    .filter(Number.isFinite);
  if (!ids.length) return;

  const firstStock = visibleStocks[0];
  this.$router.push({
    name: "Analyzer",
    params: {
      idstocks: String(ids[0]),
      stocks_name: String(firstStock?.stocks_name || "AnalyzeAllStocks"),
    },
    query: { ids: ids.join(","), prozent: "1", mode: "day" },
  });
},

  getAnalyzerDepotIdsForUsers(userIds) {
    const wanted = new Set((userIds || []).map(Number));

    const ids = (this.stocksRaw || [])
      .filter((s) => {
        const stockName = String(s?.stocks_name || "");
        const userId = Number(s?.user_iduser?.iduser);

        if (!wanted.has(userId)) return false;
        return stockName === "0_DepotKursAktuell" ||
          (userId === 1 && stockName === "0_cVirtuell");
      })
      .map(s => Number(s.idstocks))
      .filter(Number.isFinite);

    // unique
    return Array.from(new Set(ids));
  },

  matchesDashboardSearch(item) {
    const query = String(this.searchQuery || "").trim().toLowerCase();
    if (!this.showAllStocks || !query) return true;

    const searchableValues = [
      item?.stocks_name,
      item?.user_iduser?.user_username,
      item?.stocks_Kaufkurs,
      item?.stock_Aktuellerkurs,
      item?.stocks_Menge,
      item?.stock_delta1_prozent,
      item?.stock_delta5_prozent,
      item?.stock_delta20_prozent,
      item?.stock_Aktuellerkurs_gesamt,
      item?.delta,
    ];

    return searchableValues.some((value) =>
      String(value ?? "").toLowerCase().includes(query)
    );
  },

  getAggregateClassStocks() {
    const wantedNames = new Set(["0_cRohstoff", "0_cETF", "0_cAktie"]);
    const wantedUsers = new Set(this.selectedUsers.map(Number));

    return (this.stocksRaw || [])
      .filter((stock) => wantedNames.has(String(stock?.stocks_name || "")))
      .filter((stock) =>
        wantedUsers.size === 0 || wantedUsers.has(Number(stock?.user_iduser?.iduser))
      )
      .map((stock) => ({
        idstocks: Number(stock?.idstocks),
        stocks_name: String(stock?.stocks_name || ""),
      }))
      .filter((stock) => Number.isFinite(stock.idstocks));
  },

  getAggregateClassForKey(key) {
    const text = String(key || "").toLowerCase();
    if (text.includes("0_crohstoff")) return "rohstoff";
    if (text.includes("0_cetf")) return "etf";
    if (text.includes("0_caktie")) return "aktie";
    return null;
  },

  getAggregateClassForStockName(stockName) {
    if (stockName === "0_cRohstoff") return "rohstoff";
    if (stockName === "0_cETF") return "etf";
    if (stockName === "0_cAktie") return "aktie";
    return null;
  },

  async toggleAggregateChartPercent() {
    this.aggregateChartPercent = !this.aggregateChartPercent;
    await this.renderAggregateClassLineChart();
  },

  async setAggregateChartMonths(months) {
    this.aggregateChartMode = "monthly";
    this.aggregateChartMonths = Number(months);
    await this.renderAggregateClassLineChart();
  },

  async setAggregateChartDaily() {
    this.aggregateChartMode = "daily";
    this.aggregateChartMonths = null;
    await this.renderAggregateClassLineChart();
  },

  async setAggregateChartMonthly() {
    this.aggregateChartMode = "monthly";
    this.aggregateChartMonths = null;
    await this.renderAggregateClassLineChart();
  },

  toFiniteNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const numberValue = Number(String(value).replace(",", "."));
    return Number.isFinite(numberValue) ? numberValue : null;
  },

  getValueCaseInsensitive(row, wantedKeys) {
    if (!row || typeof row !== "object") return null;

    const wanted = new Set(wantedKeys.map((key) => String(key).toLowerCase()));
    const foundKey = Object.keys(row).find((key) => wanted.has(String(key).toLowerCase()));

    return foundKey ? row[foundKey] : null;
  },

  getAggregateRowDate(row) {
    return String(
      this.getValueCaseInsensitive(row, [
        "datum",
        "date",
        "stocks_datum",
        "stocks_kaufdatum",
        "stockhistory_datum",
      ]) || ""
    ).slice(0, 10);
  },

  getAggregateRowClass(row, stockIdToClass) {
    if (row?._aggregateClass) return row._aggregateClass;

    const rowStockId = Number(
      this.getValueCaseInsensitive(row, ["stocks_idstocks", "idstocks", "stock_idstocks"])
    );

    if (stockIdToClass.has(rowStockId)) return stockIdToClass.get(rowStockId);

    return this.getAggregateClassForKey(
      this.getValueCaseInsensitive(row, ["stocks_name", "stock_name", "name"])
    );
  },

  getAggregateRowKurs(row) {
    return this.toFiniteNumber(
      this.getValueCaseInsensitive(row, [
        "stocks_kurs",
        "stock_kurs",
        "stocks_Kurs",
        "stock_Aktuellerkurs",
        "stock_Aktuellerkurs_gesamt",
        "value",
        "kurs",
      ])
    );
  },

  buildAggregateClassTimeline(histories, classStocks) {
    const stockIdToClass = new Map(
      classStocks.map((stock) => [
        Number(stock.idstocks),
        this.getAggregateClassForKey(stock.stocks_name),
      ])
    );

    const byDate = new Map();
    const ensureRow = (date) => {
      if (!byDate.has(date)) {
        byDate.set(date, {
          datum: date,
          rohstoff: null,
          etf: null,
          aktie: null,
        });
      }
      return byDate.get(date);
    };

    for (const row of histories || []) {
      const date = this.getAggregateRowDate(row);
      if (!date) continue;

      const target = ensureRow(date);
      const rowClass = this.getAggregateRowClass(row, stockIdToClass);

      if (rowClass) {
        const numberValue = this.getAggregateRowKurs(row);
        if (numberValue !== null) {
          target[rowClass] = Number(target[rowClass] || 0) + numberValue;
        }
      }

      for (const [key, value] of Object.entries(row)) {
        if (["datum", "date", "id", "idstocks", "stocks_idstocks", "stock_idstocks"].includes(key)) continue;

        const aggregateClass = this.getAggregateClassForKey(key);
        if (!aggregateClass) continue;

        const numberValue = this.toFiniteNumber(value);
        if (numberValue === null) continue;

        target[aggregateClass] = Number(target[aggregateClass] || 0) + numberValue;
      }
    }

    return Array.from(byDate.values())
      .filter((row) =>
        ["rohstoff", "etf", "aktie"].some((key) => this.toFiniteNumber(row[key]) !== null)
      )
      .sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
  },

  filterAggregateRowsByRange(rows) {
    if (!Array.isArray(rows) || !rows.length) return [];
    if (this.aggregateChartMode === "daily") return this.filterAggregateRowsForValidDailyRange(rows);
    if (this.aggregateChartMode !== "monthly" || !this.aggregateChartMonths) return rows;

    const datedRows = rows
      .map((row) => ({ ...row, _date: new Date(row.datum) }))
      .filter((row) => !Number.isNaN(row._date.getTime()));

    if (!datedRows.length) return rows;

    const maxTime = Math.max(...datedRows.map((row) => row._date.getTime()));
    const minDate = new Date(maxTime);
    minDate.setMonth(minDate.getMonth() - Number(this.aggregateChartMonths));

    return datedRows
      .filter((row) => row._date >= minDate)
      .map((row) => {
        const cleanRow = { ...row };
        delete cleanRow._date;
        return cleanRow;
      });
  },

  filterAggregateRowsForValidDailyRange(rows) {
    const valueKeys = ["rohstoff", "etf", "aktie"];
    const firstCompleteIndex = rows.findIndex((row) =>
      valueKeys.every((key) => this.toFiniteNumber(row[key]) !== null)
    );

    if (firstCompleteIndex <= 0) return rows;

    return rows.slice(firstCompleteIndex);
  },

  getAggregateDatasetValues(rows, key) {
    const values = rows.map((row) => row[key]);
    if (!this.aggregateChartPercent) return values;

    const base = values.find((value) => {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) && numberValue !== 0;
    });

    if (!base) return values.map(() => null);

    return values.map((value) => {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? (numberValue / Number(base)) * 100 : null;
    });
  },

  formatChartDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  },

  formatChartEuro(value) {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  },

  formatChartValue(value) {
    if (this.aggregateChartPercent) {
      return `${Math.round(Number(value) * 100) / 100} %`;
    }

    return this.formatChartEuro(value);
  },

  async renderAggregateClassLineChart() {
    let el;
    try {
      el = await this.waitForCanvas("aggregateClassCanvas");
    } catch (e) {
      console.warn("Aggregate canvas nicht bereit:", e.message);
      return;
    }

    this.destroyChart("chartAggregateClasses");

    const classStocks = this.getAggregateClassStocks();
    const ids = classStocks.map((stock) => stock.idstocks);
    if (!ids.length) return;

    let histories = [];
    try {
      histories = this.aggregateChartMode === "daily"
        ? await this.$store.dispatch("getStockhistoriesDaily", {
            ids,
            days: this.aggregateChartDailyDays,
          })
        : await this.$store.dispatch("getStockhistories", ids);
    } catch (e) {
      console.warn("Aggregate Trenddaten konnten nicht geladen werden:", e);
      return;
    }

    const rows = this.filterAggregateRowsByRange(
      this.buildAggregateClassTimeline(histories, classStocks)
    );
    if (!rows.length) return;

    const labels = rows.map((row) => row.datum);
    const datasetDefaults = {
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2,
      tension: 0.25,
      spanGaps: true,
    };

    this.chartAggregateClasses = new Chart(el, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            ...datasetDefaults,
            label: this.aggregateChartPercent ? "Rohstoff (%)" : "Rohstoff",
            data: this.getAggregateDatasetValues(rows, "rohstoff"),
            borderColor: "#f2b744",
            backgroundColor: "rgba(242, 183, 68, 0.16)",
          },
          {
            ...datasetDefaults,
            label: this.aggregateChartPercent ? "ETF (%)" : "ETF",
            data: this.getAggregateDatasetValues(rows, "etf"),
            borderColor: "#62c370",
            backgroundColor: "rgba(98, 195, 112, 0.16)",
          },
          {
            ...datasetDefaults,
            label: this.aggregateChartPercent ? "Aktie (%)" : "Aktie",
            data: this.getAggregateDatasetValues(rows, "aktie"),
            borderColor: "#5aa9e6",
            backgroundColor: "rgba(90, 169, 230, 0.16)",
          },
        ],
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        scales: {
          x: {
            ticks: {
              maxTicksLimit: 10,
              callback: (value, index) => this.formatChartDate(labels[index]),
            },
            grid: {
              color: "rgba(255, 255, 255, 0.08)",
            },
          },
          y: {
            position: "right",
            ticks: {
              callback: (value) => this.formatChartValue(value),
            },
            grid: {
              color: "rgba(255, 255, 255, 0.08)",
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              boxWidth: 12,
              usePointStyle: true,
            },
          },
          tooltip: {
            callbacks: {
              title: (items) => this.formatChartDate(labels[items?.[0]?.dataIndex ?? 0]),
              label: (ctx) => `${ctx.dataset.label}: ${this.formatChartValue(ctx.parsed.y)}`,
            },
          },
        },
      },
    });
  },

  // =========================
  // Relativer Gewinn (%)
  // =========================
getRelPerfGrouped() {
  const list = this.array_allStocksFilteredSchwellwert || [];
  const byName = _.groupBy(list, "stocks_name");

  const seen = new Map();
  const makeShort = (n) => {
    const s = String(n || "").slice(0, 12);
    const c = (seen.get(s) || 0) + 1;
    seen.set(s, c);
    return c > 1 ? `${s}${c}` : s;
  };

  const rows = [];

  Object.keys(byName).forEach((name) => {
    let profitSum = 0;
    let investedSum = 0;

    for (const r of byName[name]) {
      const menge = Number(r.stocks_Menge ?? 0);
      const umrechnung = Number(r.stock_Umrechnungskurs ?? 1);
      const aktuell = Number(r.stock_Aktuellerkurs ?? 0);
      const kaufkurs = Number(r.stocks_Kaufkurs ?? 0);
      const realisiert = Number(r.stock_Realsiert ?? 0);

      const current = aktuell * umrechnung * menge;
      const invested = kaufkurs * menge;
      const profit = current - invested + realisiert;

      profitSum += profit;
      investedSum += invested;
    }

    if (investedSum > 0) {
      const perfPct = (profitSum / investedSum) * 100;
      rows.push({
        full: name,
        short: makeShort(name),
        pct: perfPct,
        eur: profitSum,
      });
    }
  });

  // Sortierung nach % (wie bisher)
  rows.sort((a, b) => b.pct - a.pct);

  return {
    labelsShort: rows.map((x) => x.short),
    labelsFull: rows.map((x) => x.full),
    dataPct: rows.map((x) => x.pct),
    dataEur: rows.map((x) => x.eur),
  };
},

async renderRelPerfChart() {
  let el;
  try {
    el = await this.waitForCanvas("barRelCanvas");
  } catch (e) {
    console.warn("RelPerf canvas nicht bereit:", e.message);
    return;
  }

  this.destroyChart("chartRel");

  const { labelsFull, labelsShort, dataPct, dataEur } = this.getRelPerfGrouped();
  if (!labelsShort.length) return;

  const colors = this.makeColors(labelsShort.length);

  this.chartRel = new Chart(el, {
    data: {
      labels: labelsShort,
      datasets: [
        // € = BAR
        {
          type: "bar",
          label: "Gewinn/Verlust (€)",
          data: dataEur,
          yAxisID: "yEur",
          backgroundColor: colors,
          borderWidth: 1,
          barThickness: 10,
          order: 2,
        },

        // % = LINE
        {
          type: "line",
          label: "Performance (%)",
          data: dataPct,
          yAxisID: "yPct",
          tension: 0.25,

          // ✅ sichtbar auf dark background
          borderColor: "rgba(255,255,255,0.85)",
          pointBackgroundColor: "rgba(255,255,255,0.95)",
          pointBorderColor: "rgba(255,255,255,0.95)",
          pointHoverBackgroundColor: "rgba(255,255,255,1)",
          pointHoverBorderColor: "rgba(255,255,255,1)",
          borderDash: [6, 4],

          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
          fill: false,
          order: 1,
        },
      ],
    },
    options: {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { bottom: 20 } },

scales: {
  x: {
    ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
  },

  // € LINKS
  yEur: {
    type: "linear",
    position: "left",
    grid: { drawOnChartArea: true },
    ticks: {
      callback: (val) =>
        new Intl.NumberFormat("de-DE", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(val),
    },
  },

  // % RECHTS
  yPct: {
    type: "linear",
    position: "right",
    beginAtZero: true,
    grid: { drawOnChartArea: false }, // keine 2. Gridlines
    ticks: {
      callback: (val) => `${Math.round(Number(val) * 10) / 10} %`,
    },
  },
},


      plugins: {
        legend: { display: true },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            title: (items) => {
              const i = items?.[0]?.dataIndex ?? 0;
              return labelsFull[i] || labelsShort[i] || "";
            },
            label: (ctx) => {
              const i = ctx.dataIndex;
              const pct = Number(dataPct[i] ?? 0);
              const eur = Number(dataEur[i] ?? 0);

              const eurFmt = new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              }).format(eur);

              if (ctx.dataset.type === "line") return `Performance: ${pct.toFixed(2)} %`;
              return `Gewinn/Verlust: ${eurFmt}`;
            },
          },
        },
      },
    },
  });
},

    formatCurrency(v) {
      const n = Number(v);

      if (!Number.isFinite(n)) return "- €";

      // keine Tausender-Trennzeichen, keine Nachkommastellen
      return `${Math.round(n)} €`;
    },


    formatPercent(v) {
      if (v === null || v === undefined || v === "") return "-";
      return `${Math.round(Number(v) * 100) / 100} %`;
    },

    formatNumber(v) {
      return new Intl.NumberFormat("de-DE").format(Number(v) || 0);
    },

    handleClickActivty (item,row) {
        //console.log('###handleClickActivty:',row.item.aktivitaeten_distance,item)
        console.log('go to analyzer with this stock :',row.item.idstocks, row.item.stocks_name)
        console.log('this.$router:',this.$router)

        this.$router.push({
          name: 'Analyzer',
          params: { idstocks: row.item.idstocks, stocks_name: row.item.stocks_name },
          query: { mode: 'day' }
        })
    },
      getUser (index) {
          //console.log('getUser:',index)
          //console.log('Users:',Object.keys(this.Users),Object.keys(this.Users)[index])
          return Object.keys(this.Users)[index]

      },

    filterByColumn (value, query, item) {
        //console.log('customfilter:',value, query, this.SchwellwertAktuellerKurs)
        //console.log('aktuellerkurs',item.stock_Aktuellerkurs,)


      const columnValue = item.user_iduser.user_username
      const columnValueKurs = item.stock_Aktuellerkurs_gesamt

      //console.log('columnvalue',columnValue)
      //console.log('UsersCheckBoxStatus',this.UsersCheckBoxStatus)


      if ((columnValue.indexOf(query) !== -1) && (columnValueKurs > this.SchwellwertAktuellerKurs)){
       console.log('TREFFER:',columnValue)
       return true
      }
      else{
       return false
       }

    },
    getColor (calories) {
      //console.log('getcolor:',calories)
        if (calories < 0) return 'text-red-lighten-1'
        else return 'text-green-lighten-1'
      },
      getText_ColorDashboar (spalte,zeile,wert) {
        //console.log('getcolor typ:',spalte,zeile,wert)
        if (zeile === 1 && spalte > 0){
        //Es ist eine Zahl: auf 2 Stellen runden
            wert = Math.ceil(wert *100)/100
            if (wert < 0){
             return 'text-red-lighten-1 text-right text-caption'
             //return  'red--text text--lighten-1 text-right'
            }
            else{
             return 'text-green-lighten-1 text-right text-caption'
            }
        }

        if (zeile > 1 && spalte > 0){
        //Es ist eine Zahl: auf 2 Stellen runden
            wert = Math.ceil(wert *100)/100
            if (wert < 0){
             return  'text-red-lighten-1 text-right text-caption'
            }
            else{
             return 'text-green-lighten-1 text-right text-caption'
            }
        }

        if (zeile === 0 &&  typeof spalte === 'undefined'){
             return  'subtitle-1 text-center'
        }

        if (spalte === 0){
             return  'text-caption'
        }


        //return  'red--text text--lighten-1'

      },
      destroyChart(chartRefName) {
  const chart = this[chartRefName];
  if (chart) {
    try {
      chart.stop();
      chart.destroy();
    } catch (e) {
      console.warn(`Destroy fehlgeschlagen für ${chartRefName}:`, e);
    }
    this[chartRefName] = null;
  }
},
      getFormatTextDashboard (spalte,zeile,wert) {
       //console.log('getFormatTextDashboard:',spalte,zeile,wert)
        var wertKeineStelle = 0

        if (zeile > 0 && spalte > 0){
        //Es ist eine Zahl: auf 2 Stellen runden
            wert = Math.ceil(wert *100)/100
            wertKeineStelle = Math.ceil(wert)
        }

        if (zeile === 0)  return wert
        else if (spalte === 0) return wert
        else if (zeile === 1) return wertKeineStelle + ' €'
        else if (zeile === 2) return wertKeineStelle + ' €'
        else return wert + ' %'
      },
      getFormatTextDashboardVirtuell (spalte,zeile,wert) {
       //console.log('getFormatTextDashboard:',spalte,zeile,wert)
        var wertKeineStelle = 0

        if (zeile > 0 && spalte > 0){
        //Es ist eine Zahl: auf 2 Stellen runden
            wert = Math.ceil(wert *100)/100
            wertKeineStelle = Math.ceil(wert)
        }

        if (zeile === 0)  return wert
        else if (spalte === 0) return wert
        else if (zeile === 1) return wertKeineStelle + ' €'
        else if (zeile === 2) return wertKeineStelle + ' €'
        else if (zeile === 3) return wertKeineStelle + ' €'
        else return wert + ' %'
      },
    makeColors(n) {
          const out = []
          const goldenAngle = 137.508 // gleichmäßigere Farbverteilung
          for (let i = 0; i < n; i++) {
            const hue = Math.round((i * goldenAngle) % 360)
            const lightness = i % 2 === 0 ? 45 : 65
            out.push(`hsl(${hue}, 80%, ${lightness}%)`)
          }
          return out
    },

getTypePieGrouped() {
  const list = this.array_allStocksFilteredSchwellwert || [];

  console.log("TypePie list sample:", list.slice(0, 5));

  const grouped = _.groupBy(
    list,
    (d) => String(d.stocks_Typ || "Unbekannt").trim()
  );

  const labels = [];
  const data = [];

  Object.keys(grouped).forEach((type) => {
    const sum = grouped[type].reduce(
      (acc, d) => acc + Number(d?.stock_Aktuellerkurs_gesamt || 0),
      0
    );

    if (sum > 0) {
      labels.push(type || "Unbekannt");
      data.push(sum);
    }
  });

  console.log("TypePie labels:", labels);
  console.log("TypePie data:", data);

  return { labels, data };
},

getBarGrouped() {
      const valueKey = 'delta'
      const list = this.array_allStocksFilteredSchwellwert || []
      const byName = _.groupBy(list, 'stocks_name')

      const labelsFull = []
      const labelsShort = []
      const data = []

      const seen = new Map()
      const makeShort = (n) => {
        const s = String(n || '').slice(0, 12)
        const c = (seen.get(s) || 0) + 1
        seen.set(s, c)
        return c > 1 ? `${s}${c}` : s
      }

      Object.keys(byName).forEach((name) => {
        const sum = byName[name].reduce((acc, d) => acc + Number(d?.[valueKey] || 0), 0)
        if (sum !== 0) {
          labelsFull.push(name)
          labelsShort.push(makeShort(name))
          data.push(sum)
        }
      })

      return { labelsFull, labelsShort, data }
    },

    async renderBarChart() {
      let el;
      try {
        el = await this.waitForCanvas("barCanvas");
      } catch (e) {
        console.warn("Bar canvas nicht bereit:", e.message);
        return;
      }

      this.destroyChart("chartBar");

      const { labelsFull, labelsShort, data } = this.getBarGrouped();
      if (!labelsShort.length) {
        console.warn("Bar: keine Daten (labelsShort leer)");
        return;
      }

      const colors = this.makeColors(labelsShort.length);

      this.chartBar = new Chart(el, {
        type: "bar",
        data: {
          labels: labelsShort, // -> jetzt auf X-Achse
          datasets: [
            {
              label: "Delta (€)",
              data,
              backgroundColor: colors,
              borderWidth: 1,
              barThickness: 10,
            },
          ],
        },
        options: {
          // indexAxis: "y",   // <-- WEG (war horizontal)
          indexAxis: "x",      // optional, default ist ohnehin "x"
          animation: false,
          responsive: true,
          maintainAspectRatio: false,

          layout: {
            padding: { bottom: 20 }, // etwas Platz für schräge Labels
          },

          scales: {
            x: {
              ticks: {
                autoSkip: false,       // alle anzeigen (wenn zu viele: wieder true)
                maxRotation: 45,       // schräg
                minRotation: 45,
              },
            },
            y: {
              ticks: {
                callback: (val) =>
                  new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 0,
                  }).format(val),
              },
            },
          },

          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const val = Number(ctx.parsed.y ?? 0); // <-- war parsed.x bei horizontal
                  const euro = new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 0,
                  }).format(val);

                  const full = labelsFull[ctx.dataIndex] || ctx.label;
                  return `${full}: ${euro}`;
                },
              },
            },
          },
        },
      });
    },

    getPieGrouped() {
      const valueKey = 'stock_Aktuellerkurs_gesamt'
      const list = this.array_allStocksFilteredSchwellwert || []
      const byName = _.groupBy(list, 'stocks_name')

      const labelsFull = []
      const labelsShort = []
      const data = []

      // (Optional) avoid duplicate short labels like "Apple" appearing twice
      const seen = new Map()
      const makeShort = (n) => {
        const s = String(n || '').slice(0, 5)
        const c = (seen.get(s) || 0) + 1
        seen.set(s, c)
        return c > 1 ? `${s}${c}` : s
      }

      Object.keys(byName).forEach(name => {
        const sum = byName[name].reduce((acc, d) => acc + Number(d?.[valueKey] || 0), 0)
        if (sum > 0) {
          labelsFull.push(name)
          labelsShort.push(makeShort(name)) // <- first 5 chars shown on chart
          data.push(sum)
        }
      })

      return { labelsFull, labelsShort, data }
    },



    RedrawPieChartNachFilter (Zeitraum) {
        console.log('RedrawPieChartNachFilter',Zeitraum)

        console.log('array_allStocksFilteredSchwellwert',this.array_allStocksFilteredSchwellwert)
        //this.getPieGrouped()
        this.renderPieChart()
    },

async renderPieChart() {
  let el;
  try {
    el = await this.waitForCanvas("pieCanvas");
  } catch (e) {
    console.warn("Pie canvas nicht bereit:", e.message);
    return;
  }

  this.destroyChart("chartPie");

  const { labelsFull, labelsShort, data } = this.getPieGrouped();
  if (!labelsShort.length) {
    console.warn("Pie: keine Daten (labelsShort leer)");
    return;
  }

  const colors = this.makeColors(labelsShort.length);

  this.chartPie = new Chart(el, {
    type: "pie",
    data: {
      labels: labelsShort,
      datasets: [{ label: "Aktueller Gesamtwert (€)", data, backgroundColor: colors, borderWidth: 1 }],
    },
    options: {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          display: false,
        },
        legend: { display: true, position: "right", labels: { boxWidth: 14 } },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const val = Number(ctx.parsed || 0);
              const total = ctx.dataset.data.reduce((a, b) => a + Number(b || 0), 0);
              const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
              const euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);
              const full = labelsFull[ctx.dataIndex] || ctx.label;
              return `${full}: ${euro} (${pct}%)`;
            },
          },
        },
      },
    },
  });
},

async renderTypePieChart() {
  let el;
  try {
    el = await this.waitForCanvas("typePieCanvas");
  } catch (e) {
    console.warn("Type pie canvas nicht bereit:", e.message);
    return;
  }

  this.destroyChart("chartTypePie");

  const { labels, data } = this.getTypePieGrouped();
  if (!labels.length) {
    console.warn("TypePie: keine Daten");
    return;
  }

  const colors = this.makeColors(labels.length);

  this.chartTypePie = new Chart(el, {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        {
          label: "Verteilung nach Aktientyp (%)",
          data,
          backgroundColor: colors,
          borderWidth: 1,
        },
      ],
    },
    plugins: [ChartDataLabels],
    options: {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "right",
          labels: { boxWidth: 14 },
        },
        datalabels: {
          color: "#fff",
          font: {
            weight: "bold",
            size: 13,
          },
          formatter: (value, ctx) => {
            const arr = ctx.chart.data.datasets[0].data || [];
            const total = arr.reduce((a, b) => a + Number(b || 0), 0);
            if (!total) return "";

            const pct = (Number(value) / total) * 100;
            if (pct < 2) return "";

            return `${pct.toFixed(1)}%`;
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const val = Number(ctx.parsed || 0);
              const total = ctx.dataset.data.reduce((a, b) => a + Number(b || 0), 0);
              const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";

              const euro = new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              }).format(val);

              return `${ctx.label}: ${pct}% (${euro})`;
            },
          },
        },
      },
    },
  });
},




waitForCanvas(refName, maxFrames = 60) {
  return new Promise((resolve, reject) => {
    let frame = 0;

    const tick = () => {
      const el = this.$refs[refName];
      const parent = el?.parentElement;

      if (el && parent) {
        const r = parent.getBoundingClientRect();
        // Parent muss eine sinnvolle Fläche haben
        if (r.width > 20 && r.height > 20) return resolve(el);
      }

      frame += 1;
      if (frame >= maxFrames) {
        return reject(new Error(`Canvas ${refName} hat keine Größe (noch 0x0)`));
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
},
getSignedClass(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (n < 0) return "text-red-lighten-1";
  if (n > 0) return "text-green-lighten-1";
  return "";
},
formatKiloEuro(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";

  const k = n / 1000;
  return `${k.toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })} K€`;
},



async getDataFromApi() {
  this.loading = true;
  this.error = null;

  // CLEAR all arrays you repopulate
  this.array_allStocks = [];
  this.array_allAggragtes = [];
  this.array_allRohstoffe = [];
  this.virtualRows = [];
  this.DepotKursRows = [];
  this.array_allKurseGesamt = [];
  this.array_allDelta = [];

  // (optional) reset headers_Aggregates (wie bei dir)
  this.headers_Aggregates = [
    { text: "stock_username", align: "left", sortable: true, value: "stock_username" },
    { text: "stocks_name", align: "left", sortable: true, value: "stocks_name" },
    { text: "Delta gesamt €", value: "stock_Aktuellerkurs", align: "right" },
    { text: "Delta T %", value: "stock_delta1_prozent", align: "right" },
    { text: "Delta W %", value: "stock_delta5_prozent", align: "right" },
    { text: "Delta M %", value: "stock_delta20_prozent", align: "right" },
  ];

  try {
    // 1) Daten holen
    await this.$store.dispatch("postStocks");
    console.log("--Got some data--");

    // 2) Nicht den Vuex-State mutieren -> Kopie + sort
    const stocksSorted = [...(this.stocks || [])].sort((a, b) =>
      String(a.stocks_name).localeCompare(String(b.stocks_name))
    );
    //console.log("##>>Sorted StockList", stocksSorted);

    // 3) temp arrays
    const array_allStocks_temp = [];
    const array_allAggragtes_temp = [];
    const array_allRohstoffe_temp = [];

    // 4) durchlaufen (WICHTIG: Element-Kopie, weil du delta ergänzt)
    for (const element of stocksSorted) {
      const e = { ...element };

      const handelsplatz = String(e.stocks_Handelsplatz || "");
      const googleId = String(e.stocks_GoogleId || "");
      const aktuellerGesamt = Number(e.stock_Aktuellerkurs_gesamt || 0);

      // Alle Stocks behalten; aktive Positionen und Schwellwert erst in der Ansicht filtern
      if (!googleId.includes("ufo")) {
        array_allStocks_temp.push(e);
      } else if (handelsplatz.includes("virtuell")) {
        array_allAggragtes_temp.push(e);
      } else if (handelsplatz.includes("AktuellerKurs")) {
        array_allAggragtes_temp.push(e);
      } else if (handelsplatz.includes("Kaufkurs")) {
        array_allAggragtes_temp.push(e);
      } else if (handelsplatz.includes("realisiert")) {
        array_allAggragtes_temp.push(e);
      }

      // Rohstoffe
      if (handelsplatz.includes("Rohstoff")) {
        array_allRohstoffe_temp.push(e);
      }

      // Delta (nur wenn Gesamt > 0)
      if (aktuellerGesamt > 0) {
        const menge = Number(e.stocks_Menge || 0);
        const umrechnung = Number(e.stock_Umrechnungskurs || 1);
        const aktuell = Number(e.stock_Aktuellerkurs || 0);
        const kaufkurs = Number(e.stocks_Kaufkurs || 0);

        e.delta = Math.ceil((aktuell * umrechnung - kaufkurs) * menge);

        this.array_allKurseGesamt.push(aktuellerGesamt);
        this.array_allDelta.push(e.delta);
      }
    }

    // 5) deine "push in array" Struktur beibehalten (du greifst später this.array_allStocks[0] ab)
    this.array_allStocks.push(array_allStocks_temp);
    this.array_allAggragtes.push(array_allAggragtes_temp);
    this.array_allRohstoffe.push(array_allRohstoffe_temp);

    // 6) Gruppierungen (wie bei dir)
    const Results = _.groupBy(array_allAggragtes_temp, "stocks_Handelsplatz");
    const RohstoffbyUsers = _.groupBy(array_allRohstoffe_temp, "user_iduser.user_username");
    this.Users = _.groupBy(array_allAggragtes_temp, "user_iduser.user_username");
    this.UsersCheckBoxStatus = [...new Set(array_allAggragtes_temp.map((stock) => Number(stock.user_iduser?.iduser)))];

    // 7) virtualRows bauen (wie bei dir)
    let row_deltaGesamt = { Typ: "All" };
    let row_deltaGesamt_NurRohstoffe = { Typ: "Rohst." };
    let row_deltaGesamt_OhneRohstoffe = { Typ: "OhRo." };
    let row_deltaT = { Typ: "Tag" };
    let row_deltaW = { Typ: "Woche" };
    let row_deltaM = { Typ: "Monat" };
    let row_Users = ["Virtuell"];

    for (const u in this.Users) {
      const row_header = { text: u, sortable: false };
      this.headers_Aggregates.push(row_header);

      let RohstofvirtuellgesamtProUser = 0;

      const virtList = (Results.virtuell || []).filter(x => x.stocks_name === "0_cVirtuell");
      for (const r in virtList) {
        const item = virtList[r];
        const tmpString = item?.user_iduser?.user_username || "";

        if (tmpString.includes(u)) {
          row_deltaGesamt[u] = item.stock_Aktuellerkurs;
          row_Users.push(tmpString);

          if (!RohstoffbyUsers[u]) {
            RohstofvirtuellgesamtProUser = 0;
          } else {
            RohstofvirtuellgesamtProUser = 0;
            RohstoffbyUsers[u].forEach((x) => {
              RohstofvirtuellgesamtProUser += Number(x.delta || 0);
            });
          }

          row_deltaGesamt_NurRohstoffe[u] = RohstofvirtuellgesamtProUser;
          row_deltaT[u] = item.stock_delta1_prozent;
          row_deltaW[u] = item.stock_delta5_prozent;
          row_deltaM[u] = item.stock_delta20_prozent;
        }
      }
    }

    // virtuelle Depotwerte ohne Rohstoffe
    for (const u in this.Users) {
      row_deltaGesamt_OhneRohstoffe[u] =
        Number(row_deltaGesamt[u] || 0) - Number(row_deltaGesamt_NurRohstoffe[u] || 0);
    }

    this.virtualRows.push(row_Users);
    this.virtualRows.push(row_deltaGesamt);
    this.virtualRows.push(row_deltaGesamt_NurRohstoffe);
    this.virtualRows.push(row_deltaGesamt_OhneRohstoffe);
    this.virtualRows.push(row_deltaT);
    this.virtualRows.push(row_deltaW);
    this.virtualRows.push(row_deltaM);

    // 8) DepotKursRows bauen (wie bei dir)
    row_deltaGesamt = { Typ: "Gesamt" };
    row_deltaT = { Typ: "Tag" };
    row_deltaW = { Typ: "Woche" };
    row_deltaM = { Typ: "Monat" };
    row_Users = ["Kurs"];
    const row_depotOhneRohstoff = { Typ: "Depot" };

    for (const u2 in this.Users) {
      const row_header = { text: u2, sortable: false };
      this.headers_Aggregates.push(row_header);

      let RohstofkursgesamtProUser = 0;

      const kursList = Results.AktuellerKurs || [];
      for (const r in kursList) {
        const item = kursList[r];
        const tmpString2 = item?.user_iduser?.user_username || "";

        if (tmpString2.includes(u2)) {
          row_deltaGesamt[u2] = item.stock_Aktuellerkurs;
          row_Users.push(" ");

          if (!RohstoffbyUsers[u2]) {
            RohstofkursgesamtProUser = 0;
          } else {
            RohstofkursgesamtProUser = 0;
            RohstoffbyUsers[u2].forEach((x) => {
              RohstofkursgesamtProUser += Number(x.stock_Aktuellerkurs_gesamt || 0);
            });
          }

          row_depotOhneRohstoff[u2] = Number(item.stock_Aktuellerkurs || 0) - RohstofkursgesamtProUser;

          row_deltaT[u2] = item.stock_delta1_prozent;
          row_deltaW[u2] = item.stock_delta5_prozent;
          row_deltaM[u2] = item.stock_delta20_prozent;
        }
      }
    }

    this.DepotKursRows.push(row_Users);
    this.DepotKursRows.push(row_deltaGesamt);
    this.DepotKursRows.push(row_depotOhneRohstoff);
    this.DepotKursRows.push(row_deltaT);
    this.DepotKursRows.push(row_deltaW);
    this.DepotKursRows.push(row_deltaM);

    // 9) Charts erst nach DOM + nächstem Frame zeichnen
    if (this.contentTab === "charts") {
      await this.renderAllCharts();
    }
  } catch (e) {
    console.error("Got nothing from server. Prompt user to check internet connection and try again", e);
    this.error = e?.message ?? String(e);
  } finally {
    this.loading = false;
  }
},

  },
};
</script>

<style scoped>
.dash {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Sticky Top Table */
.dash__top {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(var(--v-theme-surface, 18, 18, 18), 1);
  padding-top: 8px;
  padding-bottom: 8px;
  box-shadow: 0 8px 14px -14px rgba(0, 0, 0, 0.6);
}

/* Unterer Bereich jetzt als Tabs */
.dash__main-tabs {
  min-width: 0;
}

.dash__table,
.dash__charts {
  min-width: 0;
}

@media (max-width: 960px) {
  .dash__top {
    top: 0;
  }
}

/* Charts */
.dash__charts {
  min-width: 0;
}

.dashboard-chart-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 10px;
}

.dashboard-chart-actions .abtn {
  flex: 0 0 auto;
}

.dashboard-chart-actions::-webkit-scrollbar {
  height: 0;
}

.abtn {
  min-width: 0 !important;
  height: 30px !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  letter-spacing: 0.2px !important;
}

.abtn--percent {
  min-width: 34px !important;
  height: 30px !important;
  padding: 0 8px !important;
  font-size: 13px !important;
  font-weight: 600;
  opacity: 0.7;
  transition: all 0.15s ease;
}

.abtn--active {
  background: #4caf50 !important;
  color: white !important;
  opacity: 1 !important;
  box-shadow: 0 0 0 1px rgba(76,175,80,0.4);
}

.abtn--mode {
  opacity: 0.7;
  font-weight: 600;
  transition: all 0.15s ease;
}

.abtn--mode.abtn--active {
  opacity: 1;
}

.chart-box {
  width: 100%;
  position: relative;
}

.chart-box--tall {
  min-height: 420px;
}

.chart-box--timeline {
  min-height: 360px;
}

.chart-box--square {
  aspect-ratio: 1 / 1;
  max-width: 560px;
}

.chart-box--wide {
  min-height: 320px;
}

.chart-canvas {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

@media (max-width: 600px) {
  .chart-box--tall {
    min-height: 300px;
  }

  .chart-box--timeline {
    min-height: 280px;
  }

  .chart-box--wide {
    min-height: 260px;
  }

  .chart-box--square {
    max-width: 100%;
  }
}

.dashboard-controls {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 12px 16px;
  border-color: rgba(var(--v-theme-on-surface, 255, 255, 255), 0.12);
  border-radius: 12px;
}

.dashboard-controls__title {
  margin: 0 0 6px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
}

.dashboard-user-filter {
  flex: 0 1 auto;
  min-width: 0;
}

.dashboard-users {
  padding: 0;
}

/*noinspection CssUnusedSymbol*/
.dashboard-users :deep(.v-slide-group__content) {
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
}

/*noinspection CssUnusedSymbol*/
.dashboard-users :deep(.v-chip) {
  margin: 0;
}

.dashboard-controls__hint {
  margin: 2px 0 0;
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface, 255, 255, 255), 0.65);
}

.dashboard-controls__divider {
  display: none;
}

.dashboard-controls__bottom {
  display: contents;
}

.dashboard-analysis {
  flex: 1 1 340px;
  min-width: 0;
  max-width: 460px;
}

.dashboard-analysis__actions,
.dashboard-cash__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dashboard-analysis__select {
  flex: 1 1 200px;
  min-width: 0;
}

/*noinspection CssUnusedSymbol*/
.dashboard-controls__button,
.dashboard-cash__actions :deep(.v-btn) {
  min-height: 40px;
  letter-spacing: 0;
  text-transform: none;
}

.dashboard-cash {
  margin-left: auto;
  text-align: right;
}

.dashboard-cash__actions {
  justify-content: flex-end;
  min-height: 48px;
}

.ccash-current {
  font-variant-numeric: tabular-nums;
  font-size: 1.125rem;
  font-weight: 600;
  white-space: nowrap;
}

.dashboard-all-stocks-button {
  min-height: 40px;
  letter-spacing: 0;
  text-transform: none;
}

.dashboard-analysis-icon {
  display: none;
}

@media (max-width: 600px) {
  .dashboard-action-label {
    display: none;
  }

  .dashboard-analysis-icon {
    display: inline-flex;
    font-size: 18px;
  }

  .dashboard-controls {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: end;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
  }

  .dashboard-controls__title {
    margin-bottom: 3px;
    font-size: 0.72rem;
    line-height: 1.2;
  }

  .dashboard-user-filter,
  .dashboard-analysis {
    flex-basis: 100%;
    max-width: none;
  }

  .dashboard-user-filter {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dashboard-user-filter .dashboard-controls__title {
    margin: 0;
  }

  .dashboard-users {
    flex: 1;
    min-width: 0;
  }

  /*noinspection CssUnusedSymbol*/
  .dashboard-users :deep(.v-slide-group__content) {
    gap: 4px;
    padding: 0;
  }

  /*noinspection CssUnusedSymbol*/
  .dashboard-controls :deep(.v-chip) {
    height: 26px;
    padding-inline: 8px;
    font-size: 0.72rem;
  }

  .dashboard-controls__hint {
    display: none;
  }

  .dashboard-all-stocks-button {
    height: 32px;
    min-height: 32px;
    padding-inline: 6px;
    font-size: 0.65rem;
  }

  .dashboard-analysis__actions {
    flex-wrap: nowrap;
    gap: 6px;
  }

  .dashboard-analysis__select {
    flex: 1 1 0;
  }

  /*noinspection CssUnusedSymbol*/
  .dashboard-analysis__select :deep(.v-field) {
    --v-input-control-height: 40px;
    --v-field-padding-top: 2px;
    --v-field-padding-bottom: 2px;
    font-size: 0.75rem;
  }

  /*noinspection CssUnusedSymbol*/
  .dashboard-analysis__select :deep(.v-field__input) {
    min-height: 40px;
    row-gap: 2px;
    padding-inline: 8px;
  }

  /*noinspection CssUnusedSymbol*/
  .dashboard-controls__button,
  .dashboard-cash__actions :deep(.v-btn) {
    height: 32px;
    min-height: 32px;
    min-width: 0;
    width: 32px;
    padding: 0;
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  /*noinspection CssUnusedSymbol*/
  .dashboard-cash__actions :deep(.v-btn) {
    width: auto;
    padding-inline: 8px;
  }

  .dashboard-cash {
    width: auto;
    margin-left: 0;
    text-align: left;
  }

  .dashboard-cash .dashboard-controls__title {
    margin: 0 0 3px;
  }

  .dashboard-cash__actions {
    justify-content: flex-start;
    flex-wrap: nowrap;
    min-height: 40px;
    gap: 6px;
  }

  .ccash-current {
    font-size: 0.9rem;
  }
}

/* =========================
   TOP TABELLE
   ========================= */
.vtable-tight {
  width: 100%;
  max-width: 100%;
}

/*noinspection CssUnusedSymbol*/
.vtable-tight :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  max-width: 100%;
}

.vtable-tight :deep(table) {
  width: max-content;
  min-width: 100%;
  border-collapse: separate !important;
  border-spacing: 0;
}

.vtable-tight :deep(th),
.vtable-tight :deep(td) {
  padding: 0 2px !important;
  white-space: nowrap;
  font-size: 0.72rem;
  line-height: 1.2;
}

/*noinspection CssUnusedSymbol*/
.vtable-tight :deep(.v-data-table-header__content) {
  padding: 0 !important;
  margin: 0 !important;
}

.vtable-tight :deep(td) {
  letter-spacing: -0.2px;
}

/*noinspection CssUnusedSymbol*/
.vtable-tight :deep(.v-data-table-header__sort-icon) {
  display: none;
}

@media (max-width: 420px) {
  .vtable-tight :deep(th),
  .vtable-tight :deep(td) {
    padding: 0 4px !important;
    font-size: 0.72rem;
  }
}

@media (min-width: 900px) {
  .vtable-tight :deep(th),
  .vtable-tight :deep(td) {
    font-size: 1rem !important;
    line-height: 1.5 !important;
    padding: 6px 10px !important;
  }

  .vtable-tight :deep(th) {
    font-size: 1.05rem !important;
    font-weight: 600;
  }

  .vtable-tight :deep(td:first-child),
  .vtable-tight :deep(th:first-child) {
    min-width: 44px;
  }
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
}

.vtable-tight :deep(tbody td:not(:first-child)) {
  text-align: right !important;
  font-variant-numeric: tabular-nums;
}

.vtable-tight :deep(tbody td:first-child) {
  text-align: left !important;
}

/* Desktop: erste Spalte oben sticky */
@media (min-width: 601px) {
  .vtable-tight :deep(thead th:first-child) {
    position: sticky !important;
    left: 0;
    z-index: 5;
    background: rgba(var(--v-theme-surface, 18, 18, 18), 1);
  }

  .vtable-tight :deep(tbody td:first-child) {
    position: sticky !important;
    left: 0;
    z-index: 4;
    background: rgba(var(--v-theme-surface, 18, 18, 18), 1);
  }

  .vtable-tight :deep(thead th:first-child),
  .vtable-tight :deep(tbody td:first-child) {
    box-shadow: 6px 0 6px -6px rgba(0, 0, 0, 0.6);
  }
}

/* Mobile: oben KEINE sticky erste Spalte */
@media (max-width: 600px) {
  .vtable-tight :deep(thead th:first-child),
  .vtable-tight :deep(tbody td:first-child) {
    position: static !important;
    left: auto !important;
    box-shadow: none !important;
  }
}

.vtable-tight :deep(th:nth-child(4)),
.vtable-tight :deep(td:nth-child(4)) {
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}

/* Gut lesbare Prozentwerte ohne Umrandung */
.pct-pill {
  display: inline-block;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  font-variant-numeric: tabular-nums;
}

.euro-cell {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.vtable-tight :deep(td:nth-child(2)) .euro-cell,
.vtable-tight :deep(td:nth-child(3)) .euro-cell {
  font-size: 1rem;
}

/* =========================
   UNTERE TABELLE
   ========================= */
.lower-table {
  width: 100%;
  max-width: 100%;
}

/*noinspection CssUnusedSymbol*/
.lower-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  position: relative;
  touch-action: auto;
}

.lower-table :deep(table) {
  width: max-content;
  min-width: 100%;
  border-collapse: separate !important;
  border-spacing: 0;
}

/*noinspection CssUnusedSymbol*/
.lower-table :deep(th),
.lower-table :deep(td),
.lower-table :deep(.v-data-table__th),
.lower-table :deep(.v-data-table__td) {
  white-space: nowrap !important;
  word-break: normal !important;
  overflow-wrap: normal !important;
}

/* erste Spalte unten fixieren */
/*noinspection CssUnusedSymbol*/
.lower-table :deep(thead th:nth-child(1)),
.lower-table :deep(.v-data-table__th:nth-child(1)) {
  position: sticky !important;
  left: 0 !important;
  z-index: 30 !important;
  background: rgba(var(--v-theme-surface, 18, 18, 18), 1) !important;
}

/*noinspection CssUnusedSymbol*/
.lower-table :deep(tbody td:nth-child(1)),
.lower-table :deep(.v-data-table__td:nth-child(1)) {
  position: sticky !important;
  left: 0 !important;
  z-index: 25 !important;
  background: rgba(var(--v-theme-surface, 18, 18, 18), 1) !important;
}

/*noinspection CssUnusedSymbol*/
.lower-table :deep(thead th:nth-child(1)),
.lower-table :deep(tbody td:nth-child(1)),
.lower-table :deep(.v-data-table__th:nth-child(1)),
.lower-table :deep(.v-data-table__td:nth-child(1)) {
  box-shadow: 8px 0 8px -8px rgba(0, 0, 0, 0.65);
}

/*noinspection CssUnusedSymbol*/
.lower-table :deep(thead th:nth-child(1)),
.lower-table :deep(tbody td:nth-child(1)),
.lower-table :deep(.v-data-table__th:nth-child(1)),
.lower-table :deep(.v-data-table__td:nth-child(1)) {
  text-align: left !important;
  min-width: 132px;
  max-width: 132px;
}

.lower-table :deep(thead th:not(:nth-child(1))),
.lower-table :deep(tbody td:not(:nth-child(1))) {
  text-align: right !important;
}

.stock-name-cell {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .lower-table :deep(th),
  .lower-table :deep(td) {
    padding: 4px 8px !important;
    font-size: 0.78rem !important;
    line-height: 1.2 !important;
  }

  /*noinspection CssUnusedSymbol*/
  .lower-table :deep(thead th:nth-child(1)),
  .lower-table :deep(tbody td:nth-child(1)),
  .lower-table :deep(.v-data-table__th:nth-child(1)),
  .lower-table :deep(.v-data-table__td:nth-child(1)) {
    min-width: 120px;
    max-width: 120px;
  }

  .chart-box--tall {
    min-height: 300px;
  }
}
</style>
